import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api, formatEUR, formatDateFR } from "../lib/api";
import { Loader2, CheckCircle2, XCircle, FileText, CalendarDays, Car } from "lucide-react";

const STATUS_META = {
  accepted: { label: "Devis accepté", cls: "bg-emerald-50 border-emerald-200 text-emerald-700", icon: CheckCircle2 },
  declined: { label: "Devis refusé", cls: "bg-red-50 border-red-200 text-red-700", icon: XCircle },
  expired: { label: "Devis expiré", cls: "bg-slate-100 border-slate-200 text-slate-500", icon: XCircle },
};

export default function QuotePublic() {
  const { quoteId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [comment, setComment] = useState("");
  const [acting, setActing] = useState(null); // "accept" | "decline"
  const [result, setResult] = useState(null); // { status, booking_reference }

  useEffect(() => {
    api.get(`/public/quotes/${quoteId}`)
      .then(({ data }) => setData(data))
      .catch((err) => setError(err.response?.data?.detail || "Devis introuvable"))
      .finally(() => setLoading(false));
  }, [quoteId]);

  const respond = async (action) => {
    if (action === "decline" && !window.confirm("Confirmer le refus de ce devis ?")) return;
    setActing(action);
    try {
      const { data: r } = await api.post(`/public/quotes/${quoteId}/respond`, { action, comment });
      setResult(r);
    } catch (err) {
      setError(err.response?.data?.detail || "Une erreur est survenue");
    } finally {
      setActing(null);
    }
  };

  if (loading) {
    return <Shell><div className="py-32 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-violet-600" /></div></Shell>;
  }
  if (!data) {
    return <Shell><div className="py-24 text-center text-slate-500" data-testid="public-quote-error">{error || "Devis introuvable."}</div></Shell>;
  }

  const q = data.quote;
  const finalStatus = result?.status || (["accepted", "declined", "expired"].includes(q.status) ? q.status : null);
  const meta = finalStatus ? STATUS_META[finalStatus] : null;

  return (
    <Shell>
      <div className="max-w-2xl mx-auto px-5 py-10">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-violet-50 border border-violet-100 text-violet-700 text-xs font-bold">
            <FileText className="h-3.5 w-3.5" /> Devis {q.reference}
          </div>
          <h1 className="font-heading text-3xl sm:text-4xl font-medium tracking-tight text-slate-900 mt-4" data-testid="public-quote-title">
            Votre devis de location
          </h1>
          <p className="text-sm text-slate-500 mt-2">
            Proposé par <span className="font-semibold text-slate-700">{data.vendor.company}</span>
            {q.valid_until && <> · valable jusqu'au <span className="font-semibold text-slate-700">{formatDateFR(q.valid_until)}</span></>}
          </p>
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 p-6 sm:p-8 space-y-6" data-testid="public-quote-body">
          <div className="grid sm:grid-cols-2 gap-4">
            <InfoTile icon={Car} label="Véhicule" value={q.vehicle_name} sub={q.vehicle_category} />
            <InfoTile icon={CalendarDays} label="Période" value={`${formatDateFR(q.start_date)} → ${formatDateFR(q.end_date)}`} sub={`${q.days} jour${q.days > 1 ? "s" : ""}`} />
          </div>

          <div className="rounded-xl bg-slate-50 border border-slate-100 p-4 space-y-2 text-sm" data-testid="public-quote-totals">
            <Row label={`Location (${q.days} j × ${formatEUR(q.price_per_day)})`} val={formatEUR(q.days * q.price_per_day)} />
            {(q.options || []).map((o) => <Row key={o.key || o.label} label={o.label} val={`+ ${formatEUR(o.amount)}`} />)}
            {q.discount_amount > 0 && <Row label="Remise" val={`− ${formatEUR(q.discount_amount)}`} />}
            <Row label="Total HT" val={formatEUR(q.total_ht)} />
            <Row label={`TVA ${q.vat_rate} %`} val={formatEUR(q.vat_amount)} />
            <Row label="Total TTC" val={formatEUR(q.total_ttc)} strong />
          </div>

          {q.notes && (
            <div className="text-sm text-slate-600 bg-violet-50/50 border border-violet-100 rounded-xl p-4 whitespace-pre-wrap">{q.notes}</div>
          )}

          {meta ? (
            <div className={`rounded-xl border p-5 text-center ${meta.cls}`} data-testid="public-quote-final-status">
              <meta.icon className="h-6 w-6 mx-auto mb-2" />
              <div className="font-semibold">{meta.label}</div>
              {finalStatus === "accepted" && (
                <p className="text-xs mt-1 opacity-80">
                  Votre réservation est confirmée{result?.booking_reference ? ` (réf. ${result.booking_reference})` : ""}. Le loueur vous recontactera pour la suite.
                </p>
              )}
              {finalStatus === "expired" && <p className="text-xs mt-1 opacity-80">Contactez le loueur pour obtenir un nouveau devis.</p>}
            </div>
          ) : (
            <div className="space-y-4">
              <div>
                <label className="text-xs tracking-[0.2em] uppercase font-bold text-slate-500">Commentaire (facultatif)</label>
                <textarea
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  rows={3}
                  maxLength={1000}
                  placeholder="Une question, une précision pour le loueur…"
                  className="mt-1.5 w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:ring-2 focus:ring-violet-600 focus:border-violet-600 outline-none resize-none"
                  data-testid="public-quote-comment"
                />
              </div>
              {error && <div className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-xl px-4 py-3">{error}</div>}
              <div className="grid sm:grid-cols-2 gap-3">
                <button
                  onClick={() => respond("accept")}
                  disabled={!!acting}
                  className="h-12 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-bold transition-colors disabled:opacity-50 inline-flex items-center justify-center gap-2"
                  data-testid="public-quote-accept-btn"
                >
                  {acting === "accept" ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />} Accepter le devis
                </button>
                <button
                  onClick={() => respond("decline")}
                  disabled={!!acting}
                  className="h-12 rounded-xl border border-red-200 text-red-600 hover:bg-red-50 text-sm font-bold transition-colors disabled:opacity-50 inline-flex items-center justify-center gap-2"
                  data-testid="public-quote-decline-btn"
                >
                  {acting === "decline" ? <Loader2 className="h-4 w-4 animate-spin" /> : <XCircle className="h-4 w-4" />} Refuser
                </button>
              </div>
              <p className="text-[10px] text-slate-400 text-center">
                En acceptant, une réservation confirmée est créée automatiquement. Votre réponse est horodatée à des fins de preuve.
              </p>
            </div>
          )}
        </div>
      </div>
    </Shell>
  );
}

function Shell({ children }) {
  return <div className="min-h-screen bg-slate-50">{children}</div>;
}

function InfoTile({ icon: Icon, label, value, sub }) {
  return (
    <div className="rounded-xl border border-slate-200 p-4">
      <div className="flex items-center gap-2 text-[10px] tracking-[0.2em] uppercase font-bold text-slate-400">
        <Icon className="h-3.5 w-3.5 text-violet-600" /> {label}
      </div>
      <div className="text-sm font-semibold text-slate-900 mt-1.5">{value}</div>
      {sub && <div className="text-xs text-slate-500 mt-0.5">{sub}</div>}
    </div>
  );
}

function Row({ label, val, strong }) {
  return (
    <div className={`flex items-center justify-between ${strong ? "pt-2 border-t border-slate-200 font-semibold text-slate-900" : "text-slate-600"}`}>
      <span>{label}</span>
      <span>{val}</span>
    </div>
  );
}
